import { el } from '../core/dom.js';
import { localDate, nowIso } from '../core/time.js';
import { reflections } from '../storage/db.js';
import { uuid } from '../core/ids.js';
import type { HabitRecord } from '../core/types.js';

const STORAGE_PREFIX = 'wean.daily.ritual.v1';

interface RitualRecord {
  date: string;
  intention?: string;
  closeoutDone?: boolean;
}

function keyFor(habitId: string): string {
  return `${STORAGE_PREFIX}.${habitId}.${localDate()}`;
}

function load(habitId: string): RitualRecord {
  try {
    const raw = localStorage.getItem(keyFor(habitId));
    if (!raw) return { date: localDate() };
    return JSON.parse(raw) as RitualRecord;
  } catch { return { date: localDate() }; }
}

function save(habitId: string, rec: RitualRecord): void {
  try { localStorage.setItem(keyFor(habitId), JSON.stringify(rec)); }
  catch { /* ignore */ }
}

const INTENTION_OPTIONS = [
  { value: 'wait_once', label: 'Wait once today' },
  { value: 'stay_in_limit', label: 'Stay within the limit' },
  { value: 'avoid_one_purchase', label: 'Avoid one purchase' },
  { value: 'rescue_first', label: 'Use rescue before acting' },
  { value: 'log_honestly', label: 'Just log honestly' },
];

const CLOSEOUT_WORDS = ['grateful', 'tired', 'proud', 'frustrated', 'steady', 'hopeful', 'sad', 'calm'];

interface RenderResult {
  node: HTMLElement | null;
  /** True when the user has already engaged today; caller can skip rendering. */
  done: boolean;
}

export function renderIntentionCard(habit: HabitRecord, nowMs: number = Date.now()): RenderResult {
  const hours = new Date(nowMs).getHours();
  if (hours >= 14) return { node: null, done: false };
  const rec = load(habit.id);
  if (rec.intention) return { node: null, done: true };
  const card = el('section', {
    class: 'card',
    attrs: { 'aria-label': 'Today\'s intention', style: 'border-color:var(--accent-soft);' },
  });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Today'));
  card.appendChild(el('h2', { attrs: { style: 'margin:0;font-size:1rem;' } }, 'What is today\'s tiny goal?'));
  const row = el('div', { class: 'kbd-list', attrs: { style: 'flex-wrap:wrap;gap:0.4rem;margin-top:0.5rem;' } });
  for (const opt of INTENTION_OPTIONS) {
    const btn = el('button', {
      class: 'tag', attrs: { type: 'button' },
    }, opt.label);
    btn.onclick = async () => {
      save(habit.id, { ...rec, intention: opt.value });
      await reflections.put({
        id: uuid(), habitId: habit.id, promptId: 'daily_intention',
        text: opt.value,
        chips: [],
        createdAt: nowIso(), localDate: localDate(),
      });
      card.remove();
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    };
    row.appendChild(btn);
  }
  card.appendChild(row);
  return { node: card, done: false };
}

export function renderCloseoutCard(habit: HabitRecord, nowMs: number = Date.now()): RenderResult {
  const hours = new Date(nowMs).getHours();
  if (hours < 19) return { node: null, done: false };
  const rec = load(habit.id);
  if (rec.closeoutDone) return { node: null, done: true };
  const card = el('section', {
    class: 'card',
    attrs: { 'aria-label': 'Evening closeout', style: 'border-color:var(--accent-soft);' },
  });
  card.appendChild(el('div', {
    class: 'muted',
    attrs: { style: 'text-transform:uppercase;letter-spacing:0.06em;font-size:0.72rem;margin-bottom:0.35rem;' },
  }, 'Evening'));
  card.appendChild(el('h2', { attrs: { style: 'margin:0;font-size:1rem;' } }, 'Want to close the day?'));
  card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin:0.35rem 0;font-size:0.85rem;' } },
    'One word for today. We will not nag.'));
  const row = el('div', { class: 'kbd-list', attrs: { style: 'flex-wrap:wrap;gap:0.4rem;margin-top:0.25rem;' } });
  for (const word of CLOSEOUT_WORDS) {
    const btn = el('button', { class: 'tag', attrs: { type: 'button' } }, word);
    btn.onclick = async () => {
      save(habit.id, { ...rec, closeoutDone: true });
      await reflections.put({
        id: uuid(), habitId: habit.id, promptId: 'daily_closeout',
        text: word,
        chips: [],
        createdAt: nowIso(), localDate: localDate(),
      });
      card.remove();
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    };
    row.appendChild(btn);
  }
  card.appendChild(row);
  const dismiss = el('button', {
    class: 'btn small ghost', attrs: { type: 'button', style: 'margin-top:0.5rem;' },
    onclick: () => {
      save(habit.id, { ...rec, closeoutDone: true });
      card.remove();
    },
  }, 'Skip tonight');
  card.appendChild(dismiss);
  return { node: card, done: false };
}
